import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useUploadFile} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const DemoUploadFile: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<string>('');

  const uploadFileMutation = useUploadFile(
    (data) => {
      setUploadResult(`✅ Upload successful: ${data.message}`);
    },
    (error) => {
      setUploadResult(`❌ Upload failed: ${error.message}`);
    }
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult('');
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadFileMutation.mutate(selectedFile);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult('');
    // Reset file input
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          📁 Upload File API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the file upload endpoint for production processing
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Upload File
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/upload/file
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Upload a file for production processing (supports Excel, PDF, and
            other document formats)
          </Typography>
        </CardContent>
      </Card>
      {/* File Upload Section */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            File Upload
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                fontSize: '14px',
              }}
            />
          </Box>

          {selectedFile && (
            <Box sx={{marginBottom: 2}}>
              <Typography
                className="typo-text body3_size"
                sx={{marginBottom: 1}}
              >
                <strong>Selected File:</strong>
              </Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Chip
                  label={selectedFile.type || 'Unknown type'}
                  color="primary"
                  size="small"
                />
                <Typography className="typo-text body3_size">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}{' '}
                  KB)
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploadFileMutation.isPending}
              sx={{minWidth: 120}}
              data-testid="demo-upload-file-button">
              {uploadFileMutation.isPending ? 'Uploading...' : 'Upload File'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={uploadFileMutation.isPending}
              data-testid="demo-upload-file-reset-button">
              Reset
            </Button>
          </Box>

          {uploadFileMutation.isPending && (
            <Box sx={{marginBottom: 2}}>
              <Typography
                className="typo-text body3_size"
                sx={{marginBottom: 1}}
              >
                Uploading file...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {uploadResult && (
            <Alert
              severity={uploadResult.includes('✅') ? 'success' : 'error'}
              sx={{marginTop: 2}}
            >
              {uploadResult}
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* Request/Response Info */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            API Details
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Request Headers:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              Content-Type: multipart/form-data
              <br />
              X-Dev-Session: 123456
              <br />
              Authorization: Bearer Bearer abc
            </Box>
          </Box>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Request Body:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              FormData with 'file' field containing the selected file
            </Box>
          </Box>

          <Box>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Format:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {`{
  "message": "string"
}`}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DemoUploadFile;
